/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Real-Time Wallet Badge & Quota Bar Component (Task 10.1.5 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useWalletBalance } from '../../hooks/useWalletBalance';
import { useUiStore } from '../../stores/useUiStore';
import { useI18n } from '../../stores/useI18nStore';
import { Coins, Plus, AlertTriangle, Sparkles, Calendar } from 'lucide-react';

export interface WalletBadgeProps {
  variant?: 'header' | 'sidebar';
  collapsed?: boolean;
}

export const WalletBadge: React.FC<WalletBadgeProps> = ({
  variant = 'header',
  collapsed = false,
}) => {
  const {
    percentageRemaining,
    percentageUsed,
    renewalDate,
    status,
    statusColor,
    formattedBalance,
    formattedQuota,
    isLowBalance,
  } = useWalletBalance();

  const { openModal } = useUiStore();
  const { t } = useI18n();

  const handleOpenTopup = (e: React.MouseEvent) => {
    e.stopPropagation();
    openModal('topup_credits');
  };

  const isLowCredits = percentageRemaining <= 15 || isLowBalance;

  // --- Variant 1: Header Compact Pill Badge ---
  if (variant === 'header') {
    return (
      <div className="relative group">
        <button
          type="button"
          onClick={handleOpenTopup}
          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl border transition-all text-xs font-medium shadow-xs focus:outline-none ${
            isLowCredits
              ? 'border-rose-500/50 bg-rose-500/10 text-rose-500 ring-2 ring-rose-500/30 animate-pulse'
              : status === 'warning'
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-500 hover:border-amber-500/70'
                : 'border-surface-border bg-surface-elevated/70 text-foreground hover:bg-surface-elevated'
          }`}
          title={`${t('wallet.title')}: ${formattedBalance} / ${formattedQuota} (${percentageRemaining}%)`}
          aria-label={t('wallet.viewWallet')}
        >
          <div className="flex items-center gap-1.5">
            <Coins className={`size-3.5 ${isLowCredits ? 'text-rose-500' : 'text-primary'}`} />
            <span className={`size-1.5 rounded-full ${statusColor.dot}`} />
          </div>

          <div className="flex items-center gap-1">
            <span className="font-bold font-mono text-xs">{formattedBalance}</span>
            <span className="text-[10px] text-foreground-muted hidden sm:inline">{t('wallet.credits')}</span>
          </div>

          {/* Quick Topup Plus Trigger */}
          <div className="pl-1 border-l border-surface-border/60">
            <div
              className={`size-4 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${
                isLowCredits ? 'bg-rose-500 text-white' : 'bg-primary/20 text-primary hover:bg-primary hover:text-primary-fg'
              }`}
            >
              <Plus className="size-2.5" />
            </div>
          </div>
        </button>

        {/* Floating Tooltip Detail on Hover */}
        <div className="absolute right-0 top-full mt-2 hidden group-hover:block z-50 w-64 p-3.5 rounded-2xl bg-surface border border-surface-border shadow-2xl text-[11px] space-y-3 pointer-events-auto animate-fade-in">
          <div className="flex items-center justify-between text-foreground">
            <span className="font-semibold">{t('wallet.title')}</span>
            <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
              {t('wallet.percentRemaining', { percent: percentageRemaining })}
            </span>
          </div>

          {/* Progress Bar (% Credit Quota Bar) */}
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden p-0.5 border border-surface-border/50">
            <div
              role="progressbar"
              aria-valuenow={percentageRemaining}
              aria-valuemin={0}
              aria-valuemax={100}
              className={`h-full rounded-full transition-all duration-500 ease-out ${
                status === 'critical'
                  ? 'bg-rose-500'
                  : status === 'warning'
                    ? 'bg-amber-500'
                    : 'bg-emerald-500'
              }`}
              style={{ width: `${percentageRemaining}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-foreground-muted text-[10px] font-mono">
            <span>{formattedBalance} / {formattedQuota}</span>
            <span>{t('wallet.percentUsed', { percent: percentageUsed })}</span>
          </div>

          {/* Renewal Date */}
          <div className="flex items-center gap-1.5 text-[10px] text-foreground-muted pt-1 border-t border-surface-border/50">
            <Calendar className="size-3 text-primary flex-shrink-0" />
            <span>{t('wallet.renewOn', { date: renewalDate })}</span>
          </div>

          {/* Gentle Warning when balance under 15% */}
          {isLowCredits && (
            <div className="flex items-start gap-1.5 p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[10px] font-medium">
              <AlertTriangle className="size-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
              <span className="leading-tight">{t('wallet.gentleWarning')}</span>
            </div>
          )}

          {/* Direct Top-Up Action Button */}
          <button
            type="button"
            onClick={handleOpenTopup}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:opacity-90 active:scale-[0.98] transition-all shadow-xs"
          >
            <Sparkles className="size-3.5" />
            <span>{t('wallet.topUpCredits')}</span>
          </button>
        </div>
      </div>
    );
  }

  // --- Variant 2: Sidebar Collapsed Icon with Status Dot ---
  if (collapsed) {
    return (
      <div className="relative group flex justify-center py-2">
        <button
          type="button"
          onClick={handleOpenTopup}
          className="relative p-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors focus:outline-none"
          title={`${t('wallet.title')}: ${formattedBalance} (${percentageRemaining}%)`}
          aria-label={t('wallet.viewWallet')}
        >
          <Coins className={`size-5 ${isLowCredits ? 'text-rose-500' : 'text-primary'}`} />
          <span className={`absolute top-1 right-1 size-2 rounded-full ring-2 ring-surface ${statusColor.dot}`} />
        </button>

        {/* Floating Tooltip in collapsed sidebar */}
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 hidden group-hover:flex flex-col z-50 w-48 p-3 rounded-xl bg-surface border border-surface-border shadow-xl text-xs space-y-1.5 pointer-events-none animate-fade-in">
          <div className="font-bold text-foreground">{t('wallet.title')}</div>
          <div className="font-mono text-primary font-semibold text-[11px]">
            {formattedBalance} / {formattedQuota}
          </div>
          <div className="flex items-center justify-between text-[10px] text-foreground-muted">
            <span>{t('wallet.percentRemaining', { percent: percentageRemaining })}</span>
            <span>{t('wallet.percentUsed', { percent: percentageUsed })}</span>
          </div>
        </div>
      </div>
    );
  }

  // --- Variant 3: Sidebar Expanded Widget Card ---
  return (
    <div
      className={`p-3.5 rounded-2xl border transition-all space-y-3 ${
        isLowCredits
          ? 'border-rose-500/30 bg-rose-500/5 ring-1 ring-rose-500/20'
          : 'border-surface-border bg-surface-elevated/50'
      }`}
    >
      {/* Header: Title & Status Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <Coins className="size-3.5 text-primary" />
          <span>{t('wallet.title')}</span>
        </div>

        <span
          className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColor.bg}`}
        >
          <span className={`size-1.5 rounded-full ${statusColor.dot}`} />
          <span>
            {status === 'critical' ? 'Depleted' : status === 'warning' ? 'Low' : 'Healthy'}
          </span>
        </span>
      </div>

      {/* Numerical Counter */}
      <div className="space-y-1">
        <div className="flex items-baseline justify-between">
          <div className="font-mono text-base font-bold text-foreground tracking-tight">
            {formattedBalance}
          </div>
          <div className="text-[10px] text-foreground-muted font-mono">
            / {formattedQuota}
          </div>
        </div>

        {/* Progress Bar (% Credit Quota Bar) */}
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden p-0.5 border border-surface-border/50">
          <div
            role="progressbar"
            aria-valuenow={percentageRemaining}
            aria-valuemin={0}
            aria-valuemax={100}
            className={`h-full rounded-full transition-all duration-500 ease-out ${
              status === 'critical'
                ? 'bg-gradient-to-r from-rose-500 to-red-600'
                : status === 'warning'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-400'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-400'
            }`}
            style={{ width: `${percentageRemaining}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-[10px] text-foreground-muted pt-0.5">
          <span>{t('wallet.percentRemaining', { percent: percentageRemaining })}</span>
          <span>{t('wallet.percentUsed', { percent: percentageUsed })}</span>
        </div>

        {/* Next Renewal Date Info */}
        <div className="flex items-center gap-1 text-[10px] text-foreground-muted pt-1">
          <Calendar className="size-2.5 text-primary flex-shrink-0" />
          <span>{t('wallet.renewOn', { date: renewalDate })}</span>
        </div>
      </div>

      {/* Gentle Warning Callout if <= 15% */}
      {isLowCredits && (
        <div className="flex items-start gap-1.5 p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[10px] font-medium">
          <AlertTriangle className="size-3 flex-shrink-0 mt-0.5 text-amber-500" />
          <span className="leading-tight">{t('wallet.gentleWarning')}</span>
        </div>
      )}

      {/* Topup Action Button */}
      <button
        type="button"
        onClick={handleOpenTopup}
        className={`w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-xl text-xs font-semibold transition-all shadow-xs ${
          isLowCredits
            ? 'bg-rose-500 text-white hover:bg-rose-600 active:scale-[0.98]'
            : 'bg-primary/10 text-primary hover:bg-primary hover:text-primary-fg active:scale-[0.98]'
        }`}
      >
        <Sparkles className="size-3.5" />
        <span>{t('wallet.topUpCredits')}</span>
      </button>
    </div>
  );
};
